
import React, { useState } from 'react';

interface IzakayaFaqProps {
  lang: 'ja' | 'en';
}

const faqs = [
  {
    questionJa: '日本語が話せなくても参加できますか？',
    questionEn: 'Can I join even if I don\'t speak Japanese?',
    answerJa: 'もちろんです！ガイドは英語対応可能で、注文や店員とのやり取りもすべてサポートします。言葉の壁を気にせず、地元の居酒屋文化をお楽しみください。',
    answerEn: 'Absolutely! Our guide is English-speaking and will handle all ordering and communication with locals. No Japanese needed — just enjoy the experience!',
  },
  {
    questionJa: '飲めないお酒が苦手な場合はどうなりますか？',
    questionEn: 'What if I don\'t drink alcohol?',
    answerJa: 'ノンアルコールドリンクも各店舗でご用意しています。事前にお知らせいただければ、ソフトドリンクや烏龍茶などで楽しめるよう手配します。',
    answerEn: 'No problem at all! Non-alcoholic options are available at every bar. Just let us know in advance and we\'ll make sure you\'re well taken care of.',
  },
  {
    questionJa: '料金に含まれるものは何ですか？',
    questionEn: 'What is included in the price?',
    answerJa: 'ガイド料、各店舗でのウェルカムドリンク（1〜2杯）、軽いおつまみが含まれます。追加の飲食は現地でのお支払いとなります。',
    answerEn: 'The price includes your guide, welcome drinks (1–2 per bar), and light snacks. Additional food and drinks can be ordered and paid on-site.',
  },
  {
    questionJa: '何人まで参加できますか？',
    questionEn: 'How many people can join?',
    answerJa: '少人数制で最大6名までです。地元の雰囲気を壊さず、ガイドとの距離も近い、プライベート感のある体験をお届けするためです。',
    answerEn: 'We keep groups small — maximum 6 people. This ensures an intimate, authentic experience without disrupting the local atmosphere.',
  },
  {
    questionJa: 'ツアーはどのくらいの時間がかかりますか？',
    questionEn: 'How long does the tour last?',
    answerJa: '通常3〜4時間です。夜19時頃スタートし、3〜4軒の居酒屋・バーをはしご酒します。終了時間は参加者の希望に合わせて柔軟に対応します。',
    answerEn: 'The tour typically lasts 3–4 hours, starting around 7 PM. We visit 3–4 bars, and the end time can be adjusted based on your preferences.',
  },
  {
    questionJa: 'キャンセルポリシーはどうなっていますか？',
    questionEn: 'What is the cancellation policy?',
    answerJa: '48時間前までのキャンセルは全額返金いたします。24時間前〜48時間前は50%、24時間以内のキャンセルはご返金できかねますのでご了承ください。',
    answerEn: 'Full refund if cancelled 48+ hours in advance. 50% refund for cancellations 24–48 hours before. No refund within 24 hours of the tour.',
  },
  {
    questionJa: '集合場所はどこですか？',
    questionEn: 'Where do we meet?',
    answerJa: '各エリアの最寄り駅（戸越銀座駅・中延駅など）の改札前でお待ち合わせします。予約確定後に詳細をWhatsAppでお送りします。',
    answerEn: 'We meet at the nearest station exit (e.g., Togoshi Ginza or Nakanobu Station). Exact meeting details will be sent via WhatsApp after booking.',
  },
  {
    questionJa: '雨の日でもツアーは開催されますか？',
    questionEn: 'Does the tour run in rainy weather?',
    answerJa: 'はい、基本的に雨天決行です。居酒屋は屋内なので雨でも快適にお楽しみいただけます。移動中は傘をご持参ください。',
    answerEn: 'Yes, tours run rain or shine! Since we spend most of the time inside izakayas, rain is no problem. Just bring an umbrella for the short walks between bars.',
  },
];

export const IzakayaFaq: React.FC<IzakayaFaqProps> = ({ lang }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const heading =
    lang === 'ja'
      ? { badge: 'よくある質問', title: 'FAQ', subtitle: 'ご参加前にご確認ください' }
      : { badge: 'FAQ', title: 'Frequently Asked Questions', subtitle: 'Everything you need to know before joining' };

  const whatsappMsg = encodeURIComponent(
    lang === 'ja'
      ? 'こんにちは！東京居酒屋ナイトツアーについて質問があります。'
      : 'Hi! I have a question about the Tokyo Izakaya Night Tour.'
  );
  const whatsappUrl = `https://wa.me/819092895522?text=${whatsappMsg}`;

  const toggle = (idx: number) => setOpenIndex(openIndex === idx ? null : idx);

  return (
    <section className="py-20 bg-stone-900">
      <div className="max-w-3xl mx-auto px-6">
        {/* Heading */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-amber-500/20 rounded-full border border-amber-500/30 mb-4">
            <i className="ri-question-answer-line text-amber-400 text-sm"></i>
            <span className="text-amber-300 text-xs font-medium tracking-widest uppercase">{heading.badge}</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">{heading.title}</h2>
          <p className="text-stone-400 text-lg">{heading.subtitle}</p>
        </div>

        {/* Accordion */}
        <div className="space-y-3">
          {faqs.map((faq, idx) => {
            const isOpen = openIndex === idx;
            const question = lang === 'ja' ? faq.questionJa : faq.questionEn;
            const answer = lang === 'ja' ? faq.answerJa : faq.answerEn;

            return (
              <div
                key={idx}
                className={`rounded-xl border transition-all duration-300 overflow-hidden ${
                  isOpen
                    ? 'border-amber-500/50 bg-stone-800/80'
                    : 'border-stone-700/50 bg-stone-800/40 hover:border-stone-600'
                }`}
              >
                <button
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer"
                  onClick={() => toggle(idx)}
                >
                  <span className={`font-semibold text-base leading-snug transition-colors duration-200 ${isOpen ? 'text-amber-300' : 'text-white'}`}>
                    {question}
                  </span>
                  <div className={`w-7 h-7 flex items-center justify-center flex-shrink-0 rounded-full transition-all duration-300 ${isOpen ? 'bg-amber-500/20 rotate-180' : 'bg-stone-700'}`}>
                    <i className={`ri-arrow-down-s-line text-lg ${isOpen ? 'text-amber-400' : 'text-stone-400'}`}></i>
                  </div>
                </button>

                <div
                  className={`transition-all duration-300 ease-in-out ${
                    isOpen ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'
                  } overflow-hidden`}
                >
                  <div className="px-6 pb-5">
                    <div className="w-full h-px bg-stone-700/60 mb-4"></div>
                    <p className="text-stone-300 text-sm leading-relaxed">{answer}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <p className="text-stone-400 text-sm mb-4">
            {lang === 'ja'
              ? 'その他のご質問はWhatsAppでお気軽にどうぞ！'
              : 'Still have questions? Feel free to ask us on WhatsApp!'}
          </p>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 hover:bg-green-400 text-white font-semibold rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-whatsapp-line text-lg"></i>
            </div>
            {lang === 'ja' ? 'WhatsAppで質問する' : 'Ask on WhatsApp'}
          </a>
        </div>
      </div>
    </section>
  );
};
